import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import love from "../../assets/svg/Blog/love.svg";
import comment from "../../assets/svg/Blog/comment.svg";
import share from "../../assets/svg/Blog/share.svg";
import AddTopic from "../../assets/svg/Blog/AddTopic.svg";
import AuthorBadge from "./AuthorBadge";

const REACTIONS = [
  { type: 0, emoji: "👍" },
  { type: 1, emoji: "❤️" },
  { type: 2, emoji: "😢" },
  { type: 3, emoji: "😠" },
];

function BlogCard({
  post,
  onReaction,
  onClick,
  createTopic,
}) {
  const [showReactions, setShowReactions] = useState(false);
  const MAX_CONTENT_LENGTH = 120;

  const handleReactionClick = (type) => {
    setShowReactions(false);
    onReaction(post.id, type);
  };

  if (createTopic) {
    return (
      <motion.div
        className="h-full min-h-[400px] w-full bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 p-8 cursor-pointer flex flex-col items-center justify-center border-2 border-dashed border-blue-100 hover:border-blue-400 group"
        whileHover={{ y: -5 }}
        whileTap={{ scale: 0.98 }}
        onClick={onClick}
      >
        <div className="w-24 h-24 mb-6 rounded-full bg-blue-50 flex items-center justify-center group-hover:bg-blue-600 transition-colors duration-300">
          <img className="w-12 h-12 grayscale group-hover:grayscale-0 group-hover:invert transition-all duration-300" src={AddTopic} alt="add" />
        </div>
        <h3 className="text-xl font-bold text-gray-800">Create New Post</h3>
        <p className="text-gray-500 text-center mt-2 px-4 italic text-sm">Share your knowledge with the community</p>
      </motion.div>
    );
  }

  const truncatedContent = post.content?.length > MAX_CONTENT_LENGTH
    ? post.content.slice(0, MAX_CONTENT_LENGTH) + "..."
    : post.content;

  return (
    <motion.div
      className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 h-[480px] flex flex-col border border-gray-100"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -8 }}
      onClick={onClick}
    >
      {/* Image Section */}
      <div className="relative h-48 overflow-hidden cursor-pointer">
        <motion.img
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          src={post.image}
          alt={post.title}
        />
        <div className="absolute top-4 left-4">
          <span className="bg-white/90 backdrop-blur-md text-blue-600 text-xs font-bold px-3 py-1.5 rounded-full shadow-sm uppercase tracking-wider">
            {post.category?.name || 'Story'}
          </span>
        </div>
      </div>

      {/* Content Section */}
      <div className="flex flex-col flex-1 p-6">
        <div className="flex-1">
          <h3 className="text-xl font-extrabold mb-3 text-gray-900 group-hover:text-blue-600 transition-colors duration-200 line-clamp-2">
            {post.title}
          </h3>
          <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-3">
            {truncatedContent}
          </p>
        </div>

        {/* Author info */}
        <AuthorBadge author={post.user} date={post.created_at} className="mb-4" />

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center space-x-4">
            {/* Reactions */}
            <div className="relative">
              <button
                className="flex items-center gap-1.5 text-gray-600 hover:text-red-500 transition-all duration-200 group/react"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowReactions(!showReactions);
                }}
              >
                <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center group-hover/react:bg-red-50 transition-colors">
                  <img src={love} alt="likes" className="w-4 h-4" />
                </div>
                <span className="text-sm font-semibold">{post.reactions_count || 0}</span>
              </button>

              <AnimatePresence>
                {showReactions && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.8, y: 10 }}
                    className="absolute bottom-full left-0 mb-3 bg-white/80 backdrop-blur-xl border border-white/20 p-2 rounded-full shadow-2xl flex space-x-2 z-10"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {REACTIONS.map((reaction) => (
                      <motion.span
                        key={reaction.type}
                        className="cursor-pointer text-xl hover:scale-150 transition-transform duration-200"
                        onClick={() => handleReactionClick(reaction.type)}
                      >
                        {reaction.emoji}
                      </motion.span>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Comments */}
            <div className="flex items-center gap-1.5 text-gray-600">
              <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center">
                <img src={comment} alt="comments" className="w-4 h-4" />
              </div>
              <span className="text-sm font-semibold">{post.comments_count || 0}</span>
            </div>
          </div>

          <button
            className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center hover:bg-blue-50 hover:text-blue-600 transition-all"
            onClick={(e) => {
              e.stopPropagation();
              // Share logic
            }}
          >
            <img src={share} alt="share" className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default React.memo(BlogCard);

